import { makeStyles, tokens } from '@fluentui/react-components';

export const FLAME_BAR_LEFT_VAR = '--flame-bar-left';
export const FLAME_BAR_END_VAR = '--flame-bar-end';
const FLAME_BAR_MIN_WIDTH = '1px';
const FLAME_BAR_HIT_MARGIN = '3px';
// Padding percentages resolve against the lane width, like the bar position.
const FLAME_BAR_INLINE_PADDING = `clamp(0px, calc((var(${FLAME_BAR_END_VAR}) - var(${FLAME_BAR_LEFT_VAR}) - 2px) / 2), 5px)`;

export const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  conversationId: {
    color: tokens.colorNeutralForeground3,
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    fontSize: tokens.fontSizeBase200,
    overflowWrap: 'anywhere',
  },
  flameCard: {
    padding: tokens.spacingHorizontalM,
    gap: tokens.spacingVerticalS,
  },
  flameHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  flameTitle: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  flameHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  flameHeaderActions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
  },
  zoomLabel: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
  },
  flameToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    minHeight: '30px',
  },
  flameFilters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  flameFilterButton: {
    padding: `2px ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
  },
  flameFilterButtonSelected: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  zoomOverview: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  resetZoomButton: {
    flexShrink: 0,
    padding: `2px ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
  },
  zoomNavigator: {
    position: 'relative',
    height: '38px',
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  zoomNavigatorBar: {
    position: 'absolute',
    height: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
  },
  zoomNavigatorShade: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 2,
    backgroundColor: tokens.colorBrandBackground2,
    opacity: 0.7,
  },
  zoomNavigatorWindow: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 3,
    borderTop: `1px solid ${tokens.colorBrandStroke1}`,
    borderBottom: `1px solid ${tokens.colorBrandStroke1}`,
    pointerEvents: 'none',
  },
  zoomNavigatorHandle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 4,
    width: '16px',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'ew-resize',
    transform: 'translateX(-50%)',
    touchAction: 'none',
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      width: '1px',
      backgroundColor: tokens.colorBrandStroke1,
      transform: 'translateX(-50%)',
    },
    '::after': {
      content: '""',
      position: 'absolute',
      top: '4px',
      left: '50%',
      width: '7px',
      height: '18px',
      borderRadius: tokens.borderRadiusSmall,
      backgroundColor: tokens.colorBrandBackground,
      transform: 'translateX(-50%)',
    },
  },
  flameViewport: {
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  flameGrid: {
    display: 'grid',
    gridTemplateColumns: 'clamp(220px, 22vw, 360px) minmax(0, 1fr)',
    minWidth: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  flameAxisLabel: {
    position: 'sticky',
    left: 0,
    zIndex: 4,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: 'none',
    borderLeft: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  flameAxis: {
    position: 'relative',
    height: '28px',
    overflow: 'hidden',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'crosshair',
    userSelect: 'none',
  },
  flameTick: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  flameTickLabel: {
    position: 'absolute',
    top: '4px',
    paddingLeft: '4px',
    color: tokens.colorNeutralForeground3,
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    fontSize: tokens.fontSizeBase100,
    whiteSpace: 'nowrap',
  },
  flameTickLabelLast: {
    transform: 'translateX(-100%)',
    paddingLeft: 0,
  },
  flameLaneLabel: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    borderTop: 'none',
    borderLeft: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
  },
  flameLaneLabelSelected: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  flameLane: {
    position: 'relative',
    height: '30px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
    cursor: 'crosshair',
    userSelect: 'none',
  },
  flameLaneSelected: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  flameRowOutsideZoom: {
    opacity: 0.45,
  },
  flameGridLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    pointerEvents: 'none',
  },
  flameSelectionWindow: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 3,
    borderLeft: `1px solid ${tokens.colorBrandStroke1}`,
    borderRight: `1px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    opacity: 0.65,
    pointerEvents: 'none',
  },
  flameBar: {
    position: 'absolute',
    top: '4px',
    bottom: '4px',
    // Like the DevTools flame chart, bars are drawn at true scale with only a
    // hairline floor; tiny events become visible by zooming in.
    left: `var(${FLAME_BAR_LEFT_VAR})`,
    width: `max(calc(var(${FLAME_BAR_END_VAR}) - var(${FLAME_BAR_LEFT_VAR})), ${FLAME_BAR_MIN_WIDTH})`,
    paddingTop: '1px',
    paddingBottom: '1px',
    paddingLeft: FLAME_BAR_INLINE_PADDING,
    paddingRight: FLAME_BAR_INLINE_PADDING,
    boxSizing: 'border-box',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  flameBarSelected: {
    zIndex: 2,
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '1px',
  },
  flameBarThin: {
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderRadius: 0,
    padding: 0,
    overflow: 'visible',
    // Invisible hit margin keeps hairline bars clickable.
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: `-${FLAME_BAR_HIT_MARGIN}`,
      right: `-${FLAME_BAR_HIT_MARGIN}`,
    },
  },
  flameBarSystem: {
    backgroundColor: tokens.colorNeutralBackground5,
  },
  flameBarUser: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  flameBarAssistant: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  flameBarToolUse: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
  },
  flameBarToolResult: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  flameBarError: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    borderTopColor: tokens.colorPaletteRedBorder2,
    borderRightColor: tokens.colorPaletteRedBorder2,
    borderBottomColor: tokens.colorPaletteRedBorder2,
    borderLeftColor: tokens.colorPaletteRedBorder2,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
  },
  flameBarOther: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  flameTooltip: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  flameTooltipLabel: {
    fontWeight: tokens.fontWeightSemibold,
  },
  flameTooltipTiming: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    columnGap: tokens.spacingHorizontalS,
  },
  flameTooltipKey: {
    color: tokens.colorNeutralForeground3,
  },
  flameTooltipSection: {
    marginTop: tokens.spacingVerticalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
  },
  event: {
    display: 'grid',
    gridTemplateColumns: '16px minmax(0, 1fr)',
    columnGap: tokens.spacingHorizontalS,
  },
  rail: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    '::after': {
      content: '""',
      position: 'absolute',
      top: '16px',
      bottom: '-8px',
      width: '2px',
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  railLast: {
    '::after': {
      display: 'none',
    },
  },
  dot: {
    width: '10px',
    height: '10px',
    marginTop: '5px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground,
    border: `2px solid ${tokens.colorNeutralBackground1}`,
    zIndex: 1,
  },
  card: {
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
  },
  cardSelected: {
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '-1px',
    boxShadow: tokens.shadow8,
  },
  headerButton: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    width: '100%',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    textAlign: 'left',
  },
  staticHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  chevron: {
    display: 'flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
  },
  eventContent: {
    marginTop: tokens.spacingVerticalS,
  },
  clampedBody: {
    display: '-webkit-box',
    overflow: 'hidden',
    color: tokens.colorNeutralForeground2,
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
  },
  eventUsage: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  eventUsageTitle: {
    display: 'block',
    marginBottom: tokens.spacingVerticalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  eventUsageGrid: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    gap: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalM}`,
    margin: 0,
  },
  eventUsageKey: {
    color: tokens.colorNeutralForeground3,
  },
  eventUsageValue: {
    margin: 0,
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  subtype: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  contentBlock: {
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  toolBlock: {
    borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
  },
  toolHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  toolName: {
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    fontWeight: tokens.fontWeightSemibold,
  },
  toolId: {
    color: tokens.colorNeutralForeground3,
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    fontSize: tokens.fontSizeBase100,
    overflowWrap: 'anywhere',
  },
  toolLinkButton: {
    padding: `2px ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForegroundLink,
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  code: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    fontFamily: "'Cascadia Code', 'Consolas', monospace",
    fontSize: tokens.fontSizeBase200,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
});
